import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { candidatosApi, type CandidatoHistorico } from '@/lib/api/candidatos';
import { useAuthStore } from '@/lib/stores/authStore';
import { Info } from '@/design-system/Info';

const CORES = ['var(--eleg)', 'var(--warn)', 'var(--accent)', 'var(--good)', 'var(--crit)'];

const ROTULO_RESULTADO: Record<string, string> = {
  eleito: 'Eleito',
  nao_eleito: 'Não eleito',
  suplente: 'Suplente',
};

export function ElegCandPage() {
  const token = useAuthStore((s) => s.token) || '';
  const [comparar, setComparar] = useState(false);
  const [cicloZonas, setCicloZonas] = useState<number | null>(null);

  const { data: candidatos = [], isLoading } = useQuery({
    queryKey: ['candidatos'],
    queryFn: () => candidatosApi.list(token),
    enabled: !!token,
  });

  // Ciclos presentes na base, do mais antigo ao mais recente
  const ciclos = useMemo(() => {
    const set = new Set<number>();
    candidatos.forEach((c) => c.candidaturas.forEach((cd) => set.add(cd.ciclo)));
    return [...set].sort((a, b) => a - b);
  }, [candidatos]);

  const cicloAtivo = cicloZonas ?? ciclos[ciclos.length - 1] ?? null;

  const { data: resultados = [] } = useQuery({
    queryKey: ['resultados-zona', cicloAtivo],
    queryFn: () => candidatosApi.resultadosPorZona(cicloAtivo!, token),
    enabled: !!token && cicloAtivo !== null,
  });

  // O primeiro candidato é o do mandato; os demais entram só na comparação
  const exibidos = comparar ? candidatos : candidatos.slice(0, 1);

  const votosDe = (c: CandidatoHistorico, ciclo: number) =>
    c.candidaturas.find((cd) => cd.ciclo === ciclo)?.votos ?? 0;

  const maxVotos = Math.max(
    1,
    ...candidatos.flatMap((c) => c.candidaturas.map((cd) => cd.votos)),
  );

  // Votos por zona no ciclo ativo, somando todos os candidatos exibidos
  const porZona = useMemo(() => {
    const idsExibidos = new Set(exibidos.map((c) => c.id));
    const mapa = new Map<string, number>();
    resultados
      .filter((r) => idsExibidos.has(r.candidato_id))
      .forEach((r) => mapa.set(r.zona, (mapa.get(r.zona) || 0) + r.votos));
    return [...mapa.entries()].sort((a, b) => b[1] - a[1]);
  }, [resultados, exibidos]);

  const maxZona = Math.max(1, ...porZona.map(([, v]) => v));

  return (
    <div className="page" id="p-eleg-cand">
      {/* Cabeçalho */}
      <div className="head">
        <div>
          <h1 style={{ color: 'var(--eleg)' }}>Séries históricas</h1>
          <p>
            {/*
              proto:755 — tese de modelagem, e o schema a cumpre:
              `numero_urna` é coluna de `eleg.candidatura` (uma por ciclo), não
              de `eleg.candidato`, que guarda a chave estável.
            */}
            Candidato com chave estável: o número de urna é atributo da candidatura em cada ciclo,
            nunca identificador, e assim a série não se corrompe quando o número muda. Somente
            leitura: o histórico vem da carga do TSE e não é editado pela interface.
          </p>
        </div>
        <div className="act">
          <button
            className={`btn ${comparar ? 'pri' : ''}`}
            onClick={() => setComparar((v) => !v)}
            disabled={candidatos.length < 2}
            title={candidatos.length < 2 ? 'Base com um único candidato carregado' : undefined}
          >
            {comparar ? 'Ver só o mandato' : 'Comparar concorrentes'}
          </button>
        </div>
      </div>

      {isLoading && <div className="empty">Carregando histórico eleitoral...</div>}

      {!isLoading && candidatos.length === 0 && (
        <div className="empty">
          Nenhum candidato na base. O histórico depende da carga de dados do TSE.
        </div>
      )}

      {!isLoading && candidatos.length > 0 && (
        <>
          {/* KPIs */}
          <div className="grid g4">
            <div className="card kpi">
              <div className="l">Candidatos na base</div>
              <div className="v">{candidatos.length}</div>
              <div className="d">
                {candidatos[0]?.municipio} · {candidatos[0]?.uf}
              </div>
            </div>
            <div className="card kpi">
              <div className="l">Ciclos carregados</div>
              <div className="v">{ciclos.length}</div>
              <div className="d">{ciclos.join(' · ') || '—'}</div>
            </div>
            <div className="card kpi">
              <div className="l">Votos do mandato</div>
              <div className="v" style={{ color: 'var(--eleg)' }}>
                {(cicloAtivo ? votosDe(candidatos[0]!, cicloAtivo) : 0).toLocaleString('pt-BR')}
              </div>
              <div className="d">no ciclo de {cicloAtivo ?? '—'}</div>
            </div>
            <div className="card kpi">
              <div className="l">Situação</div>
              <div className="v" style={{ fontSize: 18, color: 'var(--good)' }}>
                {candidatos[0]?.melhor_resultado || '—'}
              </div>
              <div className="d">{candidatos[0]?.nome}</div>
            </div>
          </div>

          {/* Série por ciclo */}
          <div className="card" style={{ marginTop: 12 }}>
            <h2>Evolução de votos por ciclo</h2>
            {ciclos.length === 0 ? (
              <div className="empty">Nenhuma candidatura registrada.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 12 }}>
                {exibidos.map((c, i) => (
                  <div key={c.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                      <b style={{ color: CORES[i % CORES.length] }}>{c.nome}</b>
                      <span style={{ color: 'var(--ink3)' }}>
                        {c.total_ciclos} ciclo(s) · {c.melhor_resultado}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 90 }}>
                      {ciclos.map((ciclo) => {
                        const v = votosDe(c, ciclo);
                        return (
                          <div key={ciclo} style={{ flex: 1, textAlign: 'center' }}>
                            <div
                              title={`${v.toLocaleString('pt-BR')} votos em ${ciclo}`}
                              style={{
                                height: `${Math.max(2, (v / maxVotos) * 70)}px`,
                                background: CORES[i % CORES.length],
                                borderRadius: '3px 3px 0 0',
                                transition: 'height .3s',
                              }}
                            />
                            <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 4 }}>
                              {ciclo}
                            </div>
                            <div style={{ fontSize: 11 }}>{v.toLocaleString('pt-BR')}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid g21" style={{ marginTop: 12 }}>
            {/* Detalhe das candidaturas */}
            <div className="card flat tw">
              <table>
                <thead>
                  <tr>
                    <th>Candidato</th>
                    <th>Ciclo</th>
                    <th>Cargo</th>
                    <th>Nº</th>
                    <th>Partido</th>
                    <th>Votos</th>
                    <th>
                      Apurado{' '}
                      <Info titulo="Diferença entre Votos e Apurado">
                        <b>Votos</b> é o total oficial da candidatura. <b>Apurado</b> é a soma dos
                        resultados por zona já carregados na base.
                      </Info>
                    </th>
                    <th>Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {exibidos.flatMap((c) =>
                    c.candidaturas.map((cd) => (
                      <tr key={cd.id} className="row">
                        <td>
                          <b>{c.nome}</b>
                        </td>
                        <td className="num">{cd.ciclo}</td>
                        <td>{cd.cargo}</td>
                        <td className="mono">{cd.numero_urna}</td>
                        <td>
                          <span className="chip neutral">{cd.partido}</span>
                        </td>
                        <td className="num">
                          <b>{cd.votos.toLocaleString('pt-BR')}</b>
                        </td>
                        <td className="num" title={`Somado de ${cd.zonas} zona(s) carregada(s)`}>
                          {cd.votos_apurados.toLocaleString('pt-BR')}
                          <span style={{ fontSize: 11, color: 'var(--ink3)' }}> / {cd.zonas}z</span>
                        </td>
                        <td>
                          <span
                            className={`chip ${
                              cd.resultado === 'eleito'
                                ? 'good'
                                : cd.resultado === 'suplente'
                                ? 'warn'
                                : 'neutral'
                            }`}
                          >
                            {ROTULO_RESULTADO[cd.resultado] || cd.resultado}
                          </span>
                        </td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>

              <div className="note" style={{ margin: 12, fontSize: 12 }}>
                Apurado menor que Votos indica zonas ainda não importadas, não divergência de
                apuração.
              </div>
            </div>

            {/* Desempenho por zona */}
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0 }}>Votos por zona</h2>
                {ciclos.length > 1 && (
                  <select
                    className="sel sm"
                    value={cicloAtivo ?? ''}
                    onChange={(e) => setCicloZonas(Number(e.target.value))}
                  >
                    {ciclos.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {porZona.length === 0 ? (
                <div className="empty">Sem resultados por zona carregados para {cicloAtivo}.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                  {porZona.map(([zona, votos]) => (
                    <div key={zona}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span>Zona {zona}</span>
                        <b className="num">{votos.toLocaleString('pt-BR')}</b>
                      </div>
                      <div className="bar" style={{ marginTop: 4 }}>
                        <i style={{ width: `${(votos / maxZona) * 100}%`, background: 'var(--eleg)' }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
